import { ErrorMessage, Field } from "formik";
import { IMAGE_URL } from "../../config";
import { useState, useRef } from "react";
import get from "lodash/get";
import set from "lodash/set";
import { formatUnderscoreToSpace } from "../../utils/text-format";

// Legacy records may store the URL wrapped in stray quote characters
// (e.g. '"https://.../logo.png"'), which breaks the <img> preview.
const sanitizeUrl = (url) =>
  typeof url === "string" ? url.trim().replace(/^["']+|["']+$/g, "") : url;

export const FormDocumentField = ({
  label = "Upload",
  name = "imageField",
  className = "",
  maxSize = 5, // Default to 5MB
  ...props
}) => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const inputRef = useRef(null);

  const uploadFile = (file, form, name) => {
    if (!file) return;

    // --- Dynamic Size Validation ---
    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > maxSize) {
      alert(`File is too large! Maximum size allowed is ${maxSize}MB.`);
      if (inputRef.current) inputRef.current.value = ""; // Clear input
      return;
    }

    setPreviewFile(URL.createObjectURL(file));

    const formData = new FormData();
    formData.append("file", file);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", IMAGE_URL);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const percent = Math.round((e.loaded / e.total) * 100);
        setUploadProgress(percent);
      }
    };

    xhr.onloadstart = () => {
      setIsUploading(true);
      setUploadProgress(0);
    };

    xhr.onload = () => {
      setIsUploading(false);
      // Clean up the object URL to avoid memory leaks
      if (previewFile) URL.revokeObjectURL(previewFile);
      setPreviewFile(null);

      if (xhr.status === 200) {
        const response = JSON.parse(xhr.responseText);
        const filePath = response?.data?.filePath;

        const fullUrl = filePath.startsWith("http")
          ? filePath
          : `https://api.requal.in${filePath}`;

        // Standard Formik way to update field
        form.setFieldValue(name, fullUrl);
      } else {
        alert("Upload failed!");
      }
    };

    xhr.onerror = () => {
      setIsUploading(false);
      setPreviewFile(null);
      alert("An error occurred during upload!");
    };

    xhr.send(formData);
  };

  const handleFileSelect = (event, form, name) => {
    const file = event.target.files[0];
    uploadFile(file, form, name);
  };

  const handleDrop = (event, form, name) => {
    event.preventDefault();
    setIsDragActive(false);

    const file = event.dataTransfer.files[0];
    uploadFile(file, form, name);
  };

  const handleRemoveImage = (form, name) => {
    form.setFieldValue(name, "");
  };

  return (
    <Field name={name}>
      {({ form, meta }) => {
        const file = get(form.values, name);
        const error = meta.touched && meta.error;

        return (
          <div className={`flex flex-col gap-2 w-full ${className}`}>
            <label className="font-medium text-gray-800 capitalize">
              {formatUnderscoreToSpace(label)}
            </label>

            {!file ? (
              <div
                className={`border-2 rounded-lg p-6 text-center transition-all w-[250px] h-[250px] relative overflow-hidden 
               ${
                 error
                   ? "border-errorColor"
                   : isDragActive
                     ? "border-primary bg-purple-50"
                     : "border-gray-300 bg-gray-50 hover:border-primary"
               }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragActive(true);
                }}
                onDragLeave={() => setIsDragActive(false)}
                onDrop={(e) => handleDrop(e, form, name)}
              >
                {isUploading && previewFile && (
                  <img
                    src={previewFile}
                    alt="uploading"
                    className="absolute inset-0 w-full h-full object-cover scale-105 blur-[3px]"
                  />
                )}

                {isUploading ? (
                  <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center text-white px-3">
                    <p className="text-sm mb-2 font-medium">Uploading...</p>
                    <div className="w-full max-w-[180px] bg-white/40 rounded-full h-[6px] overflow-hidden shadow">
                      <div
                        className="h-full rounded-full transition-all duration-200"
                        style={{
                          width: `${uploadProgress}%`,
                          background:
                            "linear-gradient(to right, #00e676, #1e9d43)",
                        }}
                      ></div>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col justify-center items-center relative z-10 w-full h-full">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-8 h-8 text-gray-400"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-3-6l-6-6m0 0l-6 6m6-6V15"
                      />
                    </svg>
                    <p className="text-gray-600 mt-2 text-fs_14">
                      Drag & drop image or{" "}
                      <span
                        onClick={() => inputRef.current.click()}
                        className="text-primary font-medium cursor-pointer"
                      >
                        Browse
                      </span>
                    </p>
                  </div>
                )}

                <input
                  ref={inputRef}
                  type="file"
                  accept="image/*,video/mp4"
                  className="hidden"
                  onChange={(event) => handleFileSelect(event, form, name)}
                />
              </div>
            ) : (
              <div className="relative w-[250px] h-[250px]">
                <img
                  src={sanitizeUrl(file)}
                  alt="Uploaded"
                  className="w-full h-full rounded-lg object-contain"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveImage(form, name)}
                  className="absolute top-2 right-2 bg-black/60 text-white text-sm px-2 py-1 rounded-full hover:bg-black"
                >
                  ✕
                </button>
              </div>
            )}

            {error && (
              <span className="text-red-500 text-sm">
                <ErrorMessage name={name} />
              </span>
            )}
          </div>
        );
      }}
    </Field>
  );
};
